import { useState } from "react";
import { 
    UserGroupIcon,
    EnvelopeIcon,
    MagnifyingGlassIcon,
    SparklesIcon,
    ChartBarIcon,
    RocketLaunchIcon
} from "@heroicons/react/24/outline";
import { userOnboarding } from "interfaces/user";
import { getAllOnboardingStorage, setOnboardingStorage } from "utils/onboardingStorage";

const OnboardingSiteAnalyse = (props: { onNext: any; onPrev: any }) => {
    const { onNext, onPrev } = props;

    const [selectedChallenge, setSelectedChallenge] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const challenges = [
        {
            id: "not-enough-customers",
            icon: UserGroupIcon,
            bgColor: "bg-orange-50",
            iconBgColor: "bg-orange-100",
            iconColor: "text-orange-600",
            title: "Croissance à accélérer",
            description: "Trouver de nouvelles façons d'élargir votre portée et attirer plus d'établissements CHR"
        },
        {
            id: "need-more-data",
            icon: ChartBarIcon,
            bgColor: "bg-yellow-50",
            iconBgColor: "bg-yellow-100",
            iconColor: "text-yellow-600",
            title: "J'ai besoin de plus de données",
            description: "Accéder à des points de données précis pour cibler et prioriser vos prospects idéaux"
        },
        {
            id: "bad-contact-data",
            icon: EnvelopeIcon,
            bgColor: "bg-blue-50",
            iconBgColor: "bg-blue-100",
            iconColor: "text-blue-600",
            title: "Données contacts sont de mauvaise qualité",
            description: "Améliorer la qualité des données pour contacter les bonnes personnes au bon moment"
        },
        {
            id: "need-ai",
            icon: SparklesIcon,
            bgColor: "bg-pink-50",
            iconBgColor: "bg-pink-100",
            iconColor: "text-pink-600",
            title: "Je dois utiliser l'IA",
            description: "Exploiter les insights pilotés par l'IA pour travailler plus intelligemment et plus rapidement"
        },
        {
            id: "outreach-not-working",
            icon: RocketLaunchIcon,
            bgColor: "bg-purple-50",
            iconBgColor: "bg-purple-100",
            iconColor: "text-purple-600",
            title: "Optimiser ma prospection",
            description: "Créer des messages personnalisés avec des points de données uniques pour capter l'attention"
        },
        {
            id: "just-exploring",
            icon: MagnifyingGlassIcon,
            bgColor: "bg-green-50",
            iconBgColor: "bg-green-100",
            iconColor: "text-green-600",
            title: "Je découvre simplement l'outil",
            description: "Explorer et découvrir comment DuckFlow s'intègre dans votre processus de vente CHR"
        }
    ];

    const handleSelect = async (challengeId: string) => {
        setSelectedChallenge(challengeId);
        setLoading(true);

        try {
            // Save the challenge
            setOnboardingStorage("mainChallenge", challengeId);

            // Get all onboarding data
            const onboardingData = getAllOnboardingStorage();

            // Send to backend
            const res = await userOnboarding({
                mainChallenge: challengeId,
                prospectingMethods: onboardingData.prospectingMethods || "",
                enrichmentTools: onboardingData.enrichmentTools || "",
                databaseType: onboardingData.databaseType || "",
                chrTypes: onboardingData.chrTypes || "",
                chrCategories: onboardingData.chrCategories || ""
            });

            setLoading(false);

            if (res) {
                // Wait a bit for visual feedback
                setTimeout(() => {
                    onNext();
                }, 500);
            } else {
                alert("Une erreur est survenue lors de la sauvegarde");
                setSelectedChallenge("");
            }
        } catch (error) {
            setLoading(false);
            console.error("Onboarding error:", error);
            alert("Une erreur est survenue");
            setSelectedChallenge("");
        }
    };

    return (
        <div className="flex flex-col items-center justify-between min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-6 py-12">
            <div className="w-full max-w-6xl">
                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 text-center">
                    Quel est le premier défi que vous souhaitez relever avec DuckFlow ?
                </h1>
                <p className="text-gray-500 mb-12 text-center max-w-2xl mx-auto">
                    Ne vous inquiétez pas si plusieurs options vous intéressent - vous pourrez toutes les explorer plus tard.
                </p>

                {/* Options Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full mb-8">
                {challenges.map((challenge) => {
                    const Icon = challenge.icon;
                    const isSelected = selectedChallenge === challenge.id;
                    
                    return (
                        <button
                            key={challenge.id}
                            onClick={() => !loading && handleSelect(challenge.id)}
                            disabled={loading}
                            className={`p-6 rounded-2xl border transition-all text-left hover:shadow-md transform hover:scale-[1.02] ${
                                isSelected
                                    ? "border-orange-400 shadow-md scale-[1.02]"
                                    : "border-gray-200 hover:border-gray-300"
                            } ${challenge.bgColor} ${loading && !isSelected ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                            <div className="flex items-start gap-4">
                                <div className={`p-4 rounded-xl ${challenge.iconBgColor}`}>
                                    <Icon className={`h-8 w-8 ${challenge.iconColor}`} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-lg mb-2 text-gray-900">
                                        {challenge.title}
                                    </h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                        {challenge.description}
                                    </p>
                                </div>
                            </div>
                            {isSelected && loading && (
                                <div className="mt-4 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                                </div>
                            )}
                        </button>
                    );
                })}
                </div>
            </div>

            {/* Bottom Navigation */}
            <div className="w-full max-w-2xl">
                <div className="flex gap-4 items-center">
                    <button
                        className="px-6 py-4 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
                        onClick={onPrev}
                        disabled={loading}
                    >
                        Précédent
                    </button>
                    {loading && (
                        <p className="text-sm text-gray-500">
                            Enregistrement en cours...
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingSiteAnalyse;
